import { Component, ReactNode, Ref } from "react";
import { MapContainer, MapContainerProps } from "react-leaflet";
import type L from "leaflet";

class MapErrorBoundary extends Component<
  {
    children: ReactNode;
    fallbackHeight?: string;
    fallbackClassName?: string;
    className?: string;
  },
  { hasError: boolean; errorKey: number }
> {
  constructor(props: {
    children: ReactNode;
    fallbackHeight?: string;
    fallbackClassName?: string;
    className?: string;
  }) {
    super(props);
    this.state = { hasError: false, errorKey: 0 };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // Log map errors for debugging but don't crash the page
    console.warn("Map error caught:", error.message);
  }

  handleRetry = () => {
    this.setState((prev) => ({ hasError: false, errorKey: prev.errorKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className={
            this.props.fallbackClassName ||
            "flex flex-col items-center justify-center gap-2 bg-gray-100 text-sm text-gray-500"
          }
          style={
            this.props.fallbackHeight
              ? { height: this.props.fallbackHeight, width: "100%" }
              : undefined
          }
        >
          <span>Map unavailable</span>
          <button
            onClick={this.handleRetry}
            className="rounded bg-gray-200 px-3 py-1 text-xs hover:bg-gray-300"
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <div key={this.state.errorKey} className={this.props.className}>
        {this.props.children}
      </div>
    );
  }
}

type MapProps = MapContainerProps & {
  height?: string;
  fallbackClassName?: string;
  ref?: Ref<L.Map | null>;
};

export function Map({
  height,
  style,
  className,
  children,
  fallbackClassName,
  ref,
  ...props
}: MapProps) {
  return (
    <MapErrorBoundary
      fallbackHeight={height}
      className={className}
      fallbackClassName={
        fallbackClassName ||
        (className
          ? `${className} flex items-center justify-center bg-gray-100 text-sm text-gray-500`
          : undefined)
      }
    >
      <MapContainer
        ref={ref}
        style={height ? { height, width: "100%", ...style } : style}
        className={className}
        {...props}
      >
        {children}
      </MapContainer>
    </MapErrorBoundary>
  );
}
